// reexport form components
// Form components
export { RadioField, CheckboxField, InputField } from "./form";

export { default as Text } from "./text";
export { default as Headline } from "./headline";
export { default as Button } from "./button";
export { default as Card } from "./card";
export { default as Modal } from "./modal";
export { default as Link } from "./link";
export { default as TextAreaField } from "./textarea";
export { default as Loader } from "./loader";
export { default as ValidationMessage } from "./validation-message";
export { default as DropdownField } from "./dropdown";
export { Popover } from "./popover";
export { IconWrapper } from "./icon-wrapper";
export { AutoCompleteField } from "./autocomplete-field";
export { DatePicker } from "./datepicker";
export { List } from "./list";

// Constants directly defined instead of imported
export const INPUTFIELD_TYPES = {
  EMAIL: "email",
  NUMBER: "number",
  PASSWORD: "password",
  TEL: "tel",
  TEXT: "text",
  SEARCH: "search",
};

export const INPUTFIELD_LABEL_TYPES = {
  ANIMATED: "animated",
  STATIC: "static",
};

export const INPUTFIELD_VALIDATION_TYPES = {
  ERROR: "error",
  SUCCESS: "success",
  WARNING: "warning",
};

export const HEADLINE_COLORS = {
  DEFAULT: "default",
  PRIMARY: "primary",
  SECONDARY: "secondary",
};

export const HEADLINE_VARIANTS = {
  H1: "h1",
  H2: "h2",
  H3: "h3",
  H4: "h4",
  H5: "h5",
  H6: "h6",
};

export const BUTTON_VARIANTS = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  TERTIARY: "tertiary",
  QUATERNARY: "quaternary",
};

export const BUTTON_SIZES = {
  LARGE: "large",
  REGULAR: "regular",
  SMALL: "small",
};

export const TEXTAREAFIELD_LABEL_TYPES = {
  ANIMATED: "animated",
  STATIC: "static",
};

export const TEXTAREAFIELD_VALIDATION_TYPES = {
  ERROR: "error",
  SUCCESS: "success",
  WARNING: "warning",
};

export const VALIDATIONMESSAGE_VARIANTS = {
  ERROR: "error",
  SUCCESS: "success",
  WARNING: "warning",
};

export const ICON_WRAPPER = {
  SIZES: {
    LARGE: "large",
    REGULAR: "regular",
    SMALL: "small",
  },
};

// Define THEME directly instead of importing
export const THEME = {
  getAnimationSpeed: (size) => {
    const speeds = {
      regular: "0.3s",
      fast: "0.15s",
      slow: "0.6s",
    };
    return speeds[size] || speeds.regular;
  },

  getFontColor: (variant) => {
    const palette = {
      default: "#1D1D1B",
      regular: "#1D1D1B",
      inverted: "#FFFFFF",
      muted: "#666666",
    };
    return palette[variant] || palette.default;
  },

  getGrayColor: (shade) => {
    const pallet = {
      50: "var(--dui-color-gray-50)",
      100: "var(--dui-color-gray-100)",
      200: "var(--dui-color-gray-200)",
      300: "var(--dui-color-gray-300)",
      400: "var(--dui-color-gray-400)",
      500: "var(--dui-color-gray-500)",
      600: "var(--dui-color-gray-600)",
      625: "var(--dui-color-gray-625)",
      650: "var(--dui-color-gray-650)",
      700: "var(--dui-color-gray-700)",
      750: "var(--dui-color-gray-750)",
      950: "var(--dui-color-gray-950)",
    };
    return pallet[shade] || pallet[500];
  },

  getPrimaryColor: (shade) => {
    const palette = {
      300: "#F91320", // RED_300
      400: "#D40511", // RED_SCARLET
      500: "#98040C", // RED_500
    };
    return palette[shade] || palette[400]; // 400 is the default (DHL Scarlet)
  },

  getSecondaryColor: (shade) => {
    const palette = {
      200: "#FFF0B2", // YELLOW_200
      300: "#FFE57F", // YELLOW_300
      400: "#FFCC00", // YELLOW_SUNFLOWER
    };
    return palette[shade] || palette[400]; // 400 is the default (DHL Sunflower)
  },

  getSuccessColor: (shade) => {
    const palette = {
      300: "#43B02A", // DHL Green 300
      400: "#00843D", // DHL Green 400 (main)
      500: "#005A30", // DHL Green 500 (dark)
    };
    return palette[shade] || palette[400];
  },

  getErrorColor: (shade) => {
    const palette = {
      300: "#F91320", // RED_300
      400: "#D40511", // RED_SCARLET
      500: "#98040C", // RED_500
    };
    return palette[shade] || palette[400];
  },

  getWarningColor: (shade) => {
    const palette = {
      200: "#FFF0B2", // YELLOW_200
      300: "#FFE57F", // YELLOW_300
      400: "#FFCC00", // YELLOW_SUNFLOWER
    };
    return palette[shade] || palette[400];
  },

  getUnit: (size) => {
    const units = {
      tiny: "var(--dui-size-space-1x)",
      small: "var(--dui-size-space-2x)",
      regular: "0.4375rem",
      medium: "0.875rem",
      large: "1.3125rem",
      xlarge: "var(--dui-size-space-32x)",
      xxlarge: "var(--dui-size-space-40x)",
    };
    return units[size] || units.regular;
  },

  getRadius: () => "4px",

  getFontFamily: () => "Delivery, sans-serif",

  getFontSize: (size) => {
    const sizes = {
      tiny: "0.75rem",
      small: "var(--dui-size-font-xs)",
      regular: "0.8125rem",
      normal: "0.875rem",
      medium: "var(--dui-size-font-md)",
      large: "var(--dui-size-font-lg)",
      xlarge: "1.375rem",
      xxlarge: "32px",
      xxxlarge: "36px",
    };
    return sizes[size] || sizes.regular;
  },

  getFontWeight: (weight) => {
    const weights = {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    };
    return weights[weight] || weights.regular;
  },

  getViewport: (size) => {
    const viewports = {
      xs: "0px",
      sm: "576px",
      md: "768px",
      lg: "992px",
      xl: "1200px",
      xxl: "1400px",
    };
    return viewports[size] || viewports.md;
  },

  getZIndex: (layer) => {
    const layers = {
      base: 0,
      above: 1,
      dropdown: 1000,
      sticky: 1200,
      fixed: 1300,
      modalBackdrop: 1400,
      modal: 1500,
      popover: 1600,
      tooltip: 1700,
    };
    return layers[layer] || layers.base;
  },
};

export * from "./icons";
